import * as React from 'react'
import { ReactNode, useState } from 'react'
import fileDownload from 'js-file-download'
import { UAParser } from 'ua-parser-js'
import * as CompareVersions from 'compare-versions'
import { Destination, IGraphArrayPayload } from '../context/AxiosContext'
import { DriveItem, Message } from '@microsoft/microsoft-graph-types'
import { Link } from '@fluentui/react'
import { AxiosInstance } from 'axios'
import LoadableText from './LoadableText'
import { formatDocumentUrl, getDownloadUrl, getDriveItemDownload } from '../helpers/documentHelper'
import { isOfficeInitialized, outlookDisplayMessageForm } from '../helpers/outlookHelper'
import useAxios from '../hooks/useAxios'
import AsyncResultStatus = Office.AsyncResultStatus

export type StealthLinkProps = {
    id?: string // Can be Message ID or DriveItem ID. Required for direct access.
    universalId?: string // The universal ID to identify duplicates.
    url?: string // The URL to open the file as a stream.
    webUrl?: string // The URL for opening in a browser.
    driveItem?: DriveItem // DriveItem may only be set if we're planning to directly consume the download URL!
    destination?: Destination
    children?: ReactNode | string | undefined
    download?: boolean // Force the item to be downloaded.
}

const getId = (props: StealthLinkProps): string => props.driveItem?.id || props.id
const getUrl = (props: StealthLinkProps): string =>
    formatDocumentUrl(props.driveItem, props.download) ||
    (!isDesktop() && props.webUrl) ||
    props.url ||
    props.webUrl ||
    ''

const getDestination = (props: StealthLinkProps): Destination => props.destination || Destination.Documents

// Some Outlooks (eg Mac) do not support blob downloads.
const isLegacyOffice = (): boolean => {
    const ua = new UAParser().getResult()
    const result =
        isOfficeInitialized() &&
        (['IE', 'Safari', 'WebKit'].includes(ua.browser.name) ||
            (ua.browser.name === 'Edge' && CompareVersions.compare(ua.browser.version, '19', '<')))
    console.log('Client is a legacy outlook client:', result)
    return result
}

const isDesktop = (): boolean => isOfficeInitialized() || new UAParser().getResult().device.type === undefined

const isOfficeUrl = (props: StealthLinkProps): boolean => {
    const result = getUrl(props).indexOf('ms-') === 0
    console.log('Url is an office url:', result)
    return result
}

const shouldWindowOpen = (props: StealthLinkProps): boolean =>
    isOfficeUrl(props) || isLegacyOffice() || (props.destination === Destination.Email && !isDesktop())

// Download file without opening a new window. Courtesy of https://stackoverflow.com/a/53230807/862825
const stealthClickWithoutWindow = (props: StealthLinkProps, axios: AxiosInstance): Promise<void> => {
    // E-mails are downloaded straight from Exchange.
    if ([Destination.Email, Destination.PrivateEmail].includes(getDestination(props))) {
        console.log('Downloading e-mail as blob using id:', props.id)
        return axios
            .get<Blob>(`/messages/${props.id}/$value`, { responseType: 'blob', baseURL: getDestination(props) })
            .then(response => {
                console.log('Serving e-mail as download:', props.id)
                fileDownload(response.data, `${props.id}.eml`)
            })
            .catch(error => {
                console.log('Could not download e-mail as blob using id', props.id, '. error:', error)
            })
    }

    // For documents, we need to get the download URL from MS Graph first, because it is only valid for a few minutes.
    console.log('Attempt to download item using driveItem:', props.driveItem?.id, ' / ', props.id)
    return (props.driveItem ? Promise.resolve(props.driveItem) : getDriveItemDownload(props.id, axios)).then(
        driveItem => {
            axios
                .get<Blob>(getDownloadUrl(driveItem), { responseType: 'blob' })
                .then(response => {
                    console.log('Serving item with size', response.data.size, 'bytes as download:', driveItem.name)
                    fileDownload(response.data, driveItem.name)
                })
                .catch(error => {
                    console.log(
                        'Could not download item using driveItem ',
                        props.driveItem?.id,
                        ' / ',
                        props.id,
                        '. error: ',
                        error,
                    )
                })
        },
    )
}

const doStealthClick = (props: StealthLinkProps, axios: AxiosInstance): Promise<void | Window> =>
    shouldWindowOpen(props)
        ? Promise.resolve(window.open(getUrl(props), !isOfficeUrl(props) ? '_blank' : undefined))
        : stealthClickWithoutWindow(props, axios)

export const stealthClick = (props: StealthLinkProps, axios: AxiosInstance): Promise<void | Window> => {
    if (!getId(props)) {
        console.log('No ID found')
        return Promise.reject()
    }

    const openCallback = (result?: Office.AsyncResult<void>) => {
        if (!result || result.status === AsyncResultStatus.Succeeded) {
            console.log('Opened e-mail')
        } else if (result) {
            console.log('Could not open e-mail:', result.error?.code, result.error?.message)
        } else {
            console.log('Could not read result status while opening e-mail')
        }
    }

    // If we have an e-mail that we might open in Outlook natively.
    if (isOfficeInitialized() && props.destination === Destination.PrivateEmail) {
        console.log('Opening e-mail in Outlook from private mailbox:', props.id)
        outlookDisplayMessageForm(props.id, openCallback)
        return Promise.resolve()
    }

    if (isOfficeInitialized() && props.destination === Destination.Email && props.universalId) {
        console.log('Downloading e-mail from shared mailbox using universalId:', props.universalId)
        return axios
            .get<IGraphArrayPayload<Message>>('/messages/', {
                baseURL: Destination.PrivateEmail,
                headers: { SuppressErrors: 'true' },
                params: {
                    $select: 'id',
                    $filter: `internetMessageId eq '${props.universalId}'`,
                },
            })
            .then(result => {
                if (result.data.value?.length > 0) {
                    console.log('Opening e-mail in Outlook using id:', result.data.value[0].id)
                    outlookDisplayMessageForm(result.data.value[0].id, openCallback)
                    return Promise.resolve()
                }
                console.log('No response data found. error?:', result.status, result.statusText)
                return Promise.reject()
            })
            .catch(error => {
                console.log('Could not download e-mail using universalId:', props.universalId, '. error:', error)
                return doStealthClick(props, axios)
            })
    }

    // If we're provided with a URL.
    if (getUrl(props)) {
        console.log('Attempting to open using url:', getUrl(props))
        return doStealthClick(props, axios)
    }

    if (getDestination(props) === Destination.Documents) {
        console.log('Attempting to open document using id:', getId(props))
        return getDriveItemDownload(getId(props), axios)
            .then(driveItem => {
                console.log('Opening document using driveItem:', getId(props))
                return doStealthClick({ id: getId(props), driveItem }, axios)
            })
            .catch(error => console.log('Could not get DriveItem', getId(props), '. error:', error))
    }

    // If it's not a document, it's an e-mail. We can't resolve e-mail URLs on the fly, because we don't have
    // enough contextual information at this point. We need the shared mailbox and HAMMOCK backend URL for that.
    console.log('Could not open item using any means:', JSON.stringify(props))
    return Promise.reject()
}

// noinspection JSUnusedGlobalSymbols
export default function StealthLink(props: StealthLinkProps) {
    const axios = useAxios()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    if (!getId(props)) {
        return <>{props.children}</>
    }

    return (
        <LoadableText isLoading={isLoading}>
            <Link
                as="span"
                onClick={() => {
                    setIsLoading(true)
                    stealthClick(props, axios).then(() => setIsLoading(false))
                }}
            >
                {props.children}
            </Link>
        </LoadableText>
    )
}
